/**
 * Delete Actions - Snippet deletion with local state update
 *
 * Calls the Block Service API to delete the snippet, then removes it from
 * the react-sweet-state store so the UI updates immediately.
 * When options (flags, intl) are provided, shows success/error flags like createActions.
 */
import type { StoreActionApi } from 'react-sweet-state';
import type { IntlShape } from 'react-intl-next';

import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { messages } from '../../modals/DeleteSnippetModal.i18n.ts';
import type { SnippetsState } from '../snippetsStore.types.ts';

/**
 * Options for deleteSnippet that enable UI feedback (success/error flags).
 */
export type DeleteSnippetOptions = {
	flags: WithFlagsProps['flags'];
	intl: IntlShape;
};

const reportError = (error: Error) => {
	getMonitoringClient().submitError(error, {
		attribution: Attribution.INTEGRATIONS,
	});
};

const displaySuccessFlag = (
	flags: WithFlagsProps['flags'],
	intl: IntlShape,
	snippetName: string,
) => {
	flags.showSuccessFlag({
		title: intl.formatMessage(messages.deleteTemplateModalSuccessFlagTitle, {
			snippetName,
		}),
		description: intl.formatMessage(messages.deleteTemplateModalSuccessFlagDescription),
		isAutoDismiss: true,
	});
};

const displayErrorFlag = (flags: WithFlagsProps['flags'], intl: IntlShape) => {
	flags.showErrorFlag({
		title: intl.formatMessage(messages.deleteTemplateModalErrorFlagTitle),
		description: intl.formatMessage(messages.deleteTemplateModalErrorFlagDescription),
		isAutoDismiss: true,
	});
};

/**
 * Actions for deleting snippets.
 */
export const deleteActions = {
	/**
	 * Deletes a snippet by ID.
	 *
	 * - Calls the Block Service API with DELETE
	 * - On success: removes the snippet from local state; shows success flag if options provided
	 * - On error: reports to monitoring, shows error flag if options provided, then re-throws
	 *
	 * @param snippetId - The snippet ID (uuidv7) to delete
	 * @param options - Optional flags and intl for success/error UI feedback
	 */
	deleteSnippet:
		(snippetId: string, options?: DeleteSnippetOptions) =>
		async ({ getState, setState }: StoreActionApi<SnippetsState>): Promise<void> => {
			try {
				await BlockServiceClient.deleteSnippet(snippetId);

				const { snippets } = getState();
				const deletedSnippet = snippets.find((s) => s.snippetId === snippetId);

				setState({
					snippets: snippets.filter((s) => s.snippetId !== snippetId),
				});

				if (options && deletedSnippet) {
					displaySuccessFlag(options.flags, options.intl, deletedSnippet.name);
				}
			} catch (thrownError) {
				const err = thrownError instanceof Error ? thrownError : new Error(String(thrownError));
				reportError(err);
				if (options) {
					displayErrorFlag(options.flags, options.intl);
				}
				throw err;
			}
		},
};
